/**
 * `<ComponentOffCard/>` — the off-state UI for a per-team component
 * (#173 PR-A4). Replaces the §10.2 "locked Pro card" pattern from the
 * IA rework.
 *
 * Per the per-component-toggling pivot (memory:
 * `feedback_per_component_toggling_over_tiers.md`):
 *
 *   * "Turned off" replaces "locked".
 *   * "An admin can turn this on" replaces "Add Pro to this team".
 *   * Click-through goes to /teams/:tid/setup/components — leads with
 *     admin role see the toggle; non-admins see an "ask your admin"
 *     empty state (PR-A5 ships the admin UI).
 *   * No trial offer. No payment gate. The admin makes the decision;
 *     pricing happens out-of-band (later) per the founder's
 *     non-tier-bundling pivot.
 *
 * Reads the registry metadata (name + description) via
 * `useTeamFeatures(teamId)` so the off-card stays in sync with the
 * canonical registry without prop-drilling. Falls back to a generic
 * "this component" copy when the registry hasn't loaded yet (e.g.,
 * race between `<ComponentGate>` rendering and the API response).
 */

import { Link } from "react-router-dom";

import { useTeamFeatures } from "../api/useTeamFeature";
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui";

interface Props {
  componentKey: string;
  teamId: string;
}

export function ComponentOffCard({ componentKey, teamId }: Props) {
  const { data } = useTeamFeatures(teamId);
  const feature = data?.features.find((f) => f.key === componentKey);

  const name = feature?.name ?? "This component";
  const description = feature?.description ?? null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{name}</CardTitle>
        <CardDescription>This component is currently turned off for your team.</CardDescription>
      </CardHeader>
      <CardContent>
        {description !== null && <p style={{ marginBottom: "0.75rem" }}>{description}</p>}
        <p style={{ marginBottom: "0.75rem" }}>
          An admin can turn this on at <code>/teams/{teamId}/setup/components</code>.
        </p>
        <Button asChild>
          <Link to={`/teams/${teamId}/setup/components`}>Take me there →</Link>
        </Button>
      </CardContent>
    </Card>
  );
}
